import React from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui';
import { GBPChecklist as GBPChecklistType } from '@/data/mocks/local';
import {
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronRight
} from 'lucide-react';

interface GBPChecklistProps {
  items: GBPChecklistType[];
}

export const GBPChecklist: React.FC<GBPChecklistProps> = ({ items }) => {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div
              className={`p-2 rounded-full ${
                item.status === 'completed'
                  ? 'bg-green-100'
                  : item.status === 'in_progress'
                  ? 'bg-blue-100'
                  : 'bg-gray-100'
              }`}
            >
              {getStatusIcon(item.status)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium">{item.task}</h4>
                <Badge variant={getPriorityColor(item.priority)}>
                  {item.priority}
                </Badge>
              </div>
              <p className="text-sm text-gray-600">{item.category}</p>
              {item.notes && (
                <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={getStatusColor(item.status)}>
              {item.status}
            </Badge>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </motion.div>
      ))}
    </div>
  );
};
